'use client';

import { cn } from '@/lib/utils';
import { ChevronDown, Loader, MoveRight } from 'lucide-react';
import React, { ButtonHTMLAttributes } from 'react';
import AnimatedArrow from './AnimatedArrow';

/* eslint-disable */
enum ButtonVariant {
    Primary = 'primary',
    Secondary = 'secondary',
    Arrow = 'arrow',
    Expand = 'expand',
    Ghost = 'ghost'
}
/* eslint-enable */

interface CustomButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'arrow' | 'expand' | 'ghost';
    children?: React.ReactElement;
    className?: string;
    textClasses?: string;
    title?: string;
    disabled?: boolean;
    submitting?: boolean;
    color?: 'white' | 'blue';
    isExpanded?: boolean;
    index?: number;
    isActive?: boolean;
}

const Button = ({
    children,
    variant = ButtonVariant.Primary,
    className,
    textClasses,
    type = 'button',
    title,
    disabled = false,
    submitting = false,
    color = 'blue',
    isExpanded = false,
    index,
    isActive = false,
    ...buttonProps
}: CustomButtonProps) => {
    const isPrimary = variant === ButtonVariant.Primary;
    const isSecondary = variant === ButtonVariant.Secondary;
    const isArrow = variant === ButtonVariant.Arrow;
    const isExpand = variant === ButtonVariant.Expand;
    const isGhost = variant === ButtonVariant.Ghost;

    const isBlue = color === 'blue';

    const buttonClasses = cn(
        'group border-none focus:outline-none flex justify-center items-center transition-all outline-none w-fit hover:opacity-90 ',
        (isPrimary || isExpand) && 'py-3',
        isSecondary && !index && 'bg-blue-100 hover:bg-primary py-5 px-16',
        index && 'border-b-[1px] border-solid py-3 justify-start items-start w-full',
        isArrow && 'border border-primary border-solid px-8 py-5',
        disabled && 'cursor-default pointer-events-none',
        disabled && isSecondary && 'bg-gray-400 text-white',
        isBlue && !isSecondary ? 'text-primary border-primary' : 'text-white border-white',
        isBlue && index && 'text-black hover:text-primary border-black',
        isBlue && index && isActive && 'text-primary',
        className,
    );

    const textStyles = cn(
        'uppercase text-h6 leading-none text-nowrap',
        index && 'max-lg:text-h4 lg:text-h3 font-bold',
        textClasses,
    );

    return (
        <button {...buttonProps} className={buttonClasses} type={type} disabled={disabled}>
            {submitting ? (
                <Loader width={24} height={24} className="animate-spin" />
            ) : (
                <div
                    className={cn(
                        (isPrimary || isExpand) &&
                            'flex items-center bg-left-bottom bg-gradient-to-r from-primary to-primary bg-[length:0%_2px] bg-no-repeat group-hover:bg-[length:100%_2px] transition-all duration-500 ease-out',
                        (isPrimary || isExpand) && !isBlue && 'from-white to-white',
                    )}
                >
                    {index && (
                        <span className="font-helveticaNeue font-medium text-sm max-xs:text-[12px] mr-2 lg:mt-1 text-nowrap">
                            {index < 10 ? `[ 0${index} ]` : `[ ${index} ]`}
                        </span>
                    )}
                    {title && <span className={textStyles}>{title}</span>}
                    {children}
                    {isPrimary && (
                        <MoveRight
                            strokeWidth={1}
                            className="transition-all duration-500 group-hover:ml-8 ml-4"
                            width={56}
                            height={48}
                        />
                    )}
                    {isArrow && <AnimatedArrow width={94} height={86} />}
                    {isExpand && (
                        <ChevronDown
                            className={cn(
                                'ml-8 scale-125 transition-transform duration-300',
                                isExpand && isExpanded && 'rotate-180',
                            )}
                            height={48}
                        />
                    )}
                    {isGhost && <></>}
                </div>
            )}
        </button>
    );
};

export default Button;
